// AGC: LocationStepEnhanced - Enhanced location step with 3 methods
// AGC: GPS, Map Search, or Address Autocomplete

import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  MapPin, 
  ChevronRight, 
  ChevronLeft, 
  Navigation, 
  Map as MapIcon,
  Search,
  Loader2
} from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { AddressAutocomplete, type AddressResult } from './AddressAutocomplete'
import { MapLocationPicker } from './MapLocationPicker'
import { PermissionBlockedModal } from '../common/PermissionBlockedModal'
import { PermissionBlockedError } from '../../services/nativeGeolocation'
import toast from 'react-hot-toast'

// =============================================================================
// AGC: TYPES
// =============================================================================

interface LocationStepEnhancedProps {
  location: string
  coordinates: [number, number]
  countryCode: string
  onLocationChange: (location: string) => void
  onCoordinatesChange: (coords: [number, number]) => void
  onNext: () => void
  onBack?: () => void
}

type LocationMethod = 'choose' | 'gps' | 'map' | 'address'

// =============================================================================
// AGC: COMPONENT
// =============================================================================

export const LocationStepEnhanced: React.FC<LocationStepEnhancedProps> = ({
  location,
  coordinates,
  countryCode,
  onLocationChange,
  onCoordinatesChange,
  onNext,
  onBack,
}) => {
  const { t } = useTranslation()
  
  const [method, setMethod] = useState<LocationMethod>('choose')
  const [isGettingLocation, setIsGettingLocation] = useState(false)
  const [showMapPicker, setShowMapPicker] = useState(false)
  const [showPermBlocked, setShowPermBlocked] = useState(false)

  // AGC: Handle GPS method
  const handleGPS = async () => {
    setMethod('gps')
    setIsGettingLocation(true)
    
    try {
      // AGC: Use nativeGeolocation service (Capacitor on native, browser API on web)
      const { getCurrentPosition: getNativePosition } = await import('../../services/nativeGeolocation')
      const position = await getNativePosition({ enableHighAccuracy: true, timeout: 10000 })
      
      const { latitude, longitude } = position
      onCoordinatesChange([latitude, longitude])
      
      // AGC: Reverse geocoding
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`,
          {
            headers: {
              'User-Agent': 'DroidFarm/1.0',
            },
          }
        )
        const data = await response.json()
        if (data.address) {
          const city = data.address.city || data.address.town || data.address.village || ''
          const state = data.address.state || ''
          onLocationChange(`${city}${city && state ? ', ' : ''}${state}`)
        }
      } catch (geoError) {
        console.warn('AGC: Could not get address from coordinates:', geoError)
        onLocationChange(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`)
      }
      
      toast.success(t('wizard.location.gpsSuccess'))
    } catch (err: any) {
      console.error('AGC: Error getting location:', err)
      if (err instanceof PermissionBlockedError) {
        setShowPermBlocked(true)
      } else {
        toast.error(err?.message || t('wizard.location.gpsError'))
      }
      setMethod('choose') // AGC: Go back to method selection
    } finally {
      setIsGettingLocation(false)
    }
  }
  
  // AGC: Handle map method
  const handleMapMethod = () => {
    setMethod('map')
    setShowMapPicker(true)
  }
  
  // AGC: Handle address method
  const handleAddressMethod = () => {
    setMethod('address')
  }
  
  // AGC: Handle address selection from autocomplete
  const handleAddressSelect = (result: AddressResult) => {
    const lat = parseFloat(result.lat)
    const lng = parseFloat(result.lon)
    
    onCoordinatesChange([lat, lng])
    
    // AGC: Format address
    const city = result.address.city || result.address.town || result.address.village || ''
    const state = result.address.state || ''
    onLocationChange(`${city}${city && state ? ', ' : ''}${state}`)
    
    console.log('AGC: Address selected:', result.display_name, lat, lng)
  }
  
  // AGC: Handle map location selection
  const handleMapLocationSelect = (location: { lat: number; lng: number; address: string }) => {
    onCoordinatesChange([location.lat, location.lng])
    onLocationChange(location.address)
    setShowMapPicker(false)
    
    console.log('AGC: Map location selected:', location)
  }
  
  // AGC: Handle back from method
  const handleBackToMethods = () => {
    setMethod('choose')
  }
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (location.trim() && coordinates[0] !== 0) {
      onNext()
    }
  }
  
  // AGC: Render method selection screen
  if (method === 'choose') {
    return (
      <div className="flex flex-col flex-1 min-h-0 px-6">
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col justify-center">
          <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mb-6 mx-auto flex-shrink-0">
            <MapPin className="w-8 h-8 text-orange-600 dark:text-orange-400" />
          </div>

          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">
            {t('wizard.location.methodTitle')}
          </h2>

          <p className="text-gray-600 dark:text-gray-400 mb-6 text-center text-sm">
            {t('wizard.location.description')}
          </p>

          <div className="space-y-3">
            <button type="button" onClick={handleGPS}
              className="w-full p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-[var(--accent-primary)] hover:bg-[var(--accent-light-bg)] transition-all text-left group">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Navigation className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-0.5">{t('wizard.location.useGPS')}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{t('wizard.location.gpsSuccess')}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[var(--accent-primary)]" />
              </div>
            </button>

            <button type="button" onClick={handleMapMethod}
              className="w-full p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-[var(--accent-primary)] hover:bg-[var(--accent-light-bg)] transition-all text-left group">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <MapIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-0.5">{t('wizard.location.searchMap')}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{t('wizard.location.selectOnMap')}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[var(--accent-primary)]" />
              </div>
            </button>

            <button type="button" onClick={handleAddressMethod}
              className="w-full p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-[var(--accent-primary)] hover:bg-[var(--accent-light-bg)] transition-all text-left group">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Search className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-0.5">{t('wizard.location.enterAddress')}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{t('wizard.location.addressPlaceholder')}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[var(--accent-primary)]" />
              </div>
            </button>
          </div>
        </div>

        <div className="flex gap-3 py-4 flex-shrink-0">
          {onBack && (
            <button type="button" onClick={onBack}
              className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center justify-center gap-2">
              <ChevronLeft className="w-5 h-5" />
              {t('common.back')}
            </button>
          )}
        </div>
      </div>
    )
  }

  // AGC: Render GPS loading screen
  if (method === 'gps' && isGettingLocation) {
    return (
      <div className="flex flex-col flex-1 min-h-0 px-6">
        <div className="flex-1 flex flex-col justify-center items-center">
          <Loader2 className="w-12 h-12 animate-spin text-[var(--accent-primary)] mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {t('wizard.location.gettingLocation')}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
            {t('common.loading')}
          </p>
        </div>
      </div>
    )
  }

  // AGC: Render address input screen
  if (method === 'address') {
    return (
      <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 px-6">
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col justify-center">
          <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mb-6 mx-auto flex-shrink-0">
            <Search className="w-8 h-8 text-purple-600 dark:text-purple-400" />
          </div>

          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">
            {t('wizard.location.enterAddress')}
          </h2>

          <p className="text-gray-600 dark:text-gray-400 mb-6 text-center text-sm">
            {t('wizard.location.addressPlaceholder')}
          </p>

          <AddressAutocomplete
            countryCode={countryCode}
            onSelect={handleAddressSelect}
            placeholder={t('wizard.location.addressPlaceholder')}
          />

          {coordinates[0] !== 0 && location && (
            <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-green-900 dark:text-green-100">{location}</p>
                  {/* AGC: Clickable coordinates — opens map picker */}
                  <button
                    type="button"
                    onClick={() => setShowMapPicker(true)}
                    className="text-xs text-green-700 dark:text-green-300 mt-1 underline decoration-dotted hover:text-green-900 dark:hover:text-green-100"
                  >
                    {coordinates[0].toFixed(4)}, {coordinates[1].toFixed(4)} — {t('wizard.location.viewOnMap')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 py-4 flex-shrink-0">
          <button type="button" onClick={handleBackToMethods}
            className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center justify-center gap-2">
            <ChevronLeft className="w-5 h-5" />
            {t('common.back')}
          </button>
          <button type="submit" disabled={!location.trim() || coordinates[0] === 0}
            className="flex-1 px-4 py-3 text-white rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ backgroundColor: 'var(--accent-primary)' }}>
            {t('common.next')}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* AGC: Map Picker Modal — reused for coordinate preview */}
        {showMapPicker && (
          <MapLocationPicker
            countryCode={countryCode}
            initialCenter={coordinates[0] !== 0 ? coordinates : undefined}
            onLocationSelect={handleMapLocationSelect}
            onClose={() => setShowMapPicker(false)}
          />
        )}
      </form>
    )
  }

  // AGC: Render GPS success or map result screen
  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 px-6">
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col justify-center">
        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6 mx-auto flex-shrink-0">
          <MapPin className="w-8 h-8 text-green-600 dark:text-green-400" />
        </div>

        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">
          {t('wizard.location.title')}
        </h2>

        <p className="text-gray-600 dark:text-gray-400 mb-6 text-center text-sm">
          {t('wizard.location.gpsSuccess')}
        </p>

        {/* AGC: Location display — coordinates are clickable to open map preview */}
        <div className="p-4 bg-white dark:bg-gray-800 border-2 border-green-500 rounded-xl">
          <div className="flex items-start gap-3">
            <MapPin className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-gray-900 dark:text-white text-lg">{location}</p>
              <button
                type="button"
                onClick={() => setShowMapPicker(true)}
                className="text-sm text-blue-600 dark:text-blue-400 mt-1 underline decoration-dotted hover:text-blue-800 dark:hover:text-blue-200 flex items-center gap-1"
              >
                <MapIcon className="w-4 h-4" />
                {coordinates[0].toFixed(4)}, {coordinates[1].toFixed(4)} — {t('wizard.location.viewOnMap')}
              </button>
            </div>
          </div>
        </div>

        <button type="button" onClick={handleBackToMethods}
          className="mt-4 text-sm text-[var(--accent-primary)] hover:opacity-80 transition-opacity">
          {t('common.back')} {t('wizard.location.methodTitle').toLowerCase()}
        </button>
      </div>

      <div className="flex gap-3 py-4 flex-shrink-0">
        <button type="button" onClick={handleBackToMethods}
          className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center justify-center gap-2">
          <ChevronLeft className="w-5 h-5" />
          {t('common.back')}
        </button>
        <button type="submit" disabled={!location.trim() || coordinates[0] === 0}
          className="flex-1 px-4 py-3 text-white rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          style={{ backgroundColor: 'var(--accent-primary)' }}>
          {t('common.next')}
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* AGC: Map Picker Modal — also used for coordinate preview */}
      {showMapPicker && (
        <MapLocationPicker
          countryCode={countryCode}
          initialCenter={coordinates[0] !== 0 ? coordinates : undefined}
          onLocationSelect={handleMapLocationSelect}
          onClose={() => setShowMapPicker(false)}
        />
      )}

      <PermissionBlockedModal
        isOpen={showPermBlocked}
        permissionType="location"
        onClose={() => setShowPermBlocked(false)}
      />
    </form>
  )
}

export default LocationStepEnhanced
